// Activity Service - Community Activity Feed

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  Unsubscribe,
  DocumentData,
  QueryConstraint,
} from '@firebase/firestore';
import { db } from './firebase';
import type {
  Activity,
  ActivityCreateInput,
  ActivityFilters,
  ActivityType,
  ActivityVisibility,
} from '../types/activity';

const ACTIVITIES_COLLECTION = 'activities';

// ============ CREATE ACTIVITY ============

export async function createActivity(
  input: ActivityCreateInput,
  userId: string,
  userName: string,
  userPhotoUrl?: string
): Promise<string> {
  const activityRef = await addDoc(collection(db, ACTIVITIES_COLLECTION), {
    type: input.type,
    userId,
    userName,
    userPhotoUrl,
    placeId: input.placeId,
    placeName: input.placeName,
    placeImageUrl: input.placeImageUrl,
    groupId: input.groupId,
    groupName: input.groupName,
    eventId: input.eventId,
    eventTitle: input.eventTitle,
    content: input.content,
    rating: input.rating,
    imageUrl: input.imageUrl,
    visibility: input.visibility || 'public',
    createdAt: serverTimestamp(),
    likeCount: 0,
    commentCount: 0,
  });

  return activityRef.id;
}

// Helper to create activity for common actions
export async function logReviewActivity(
  userId: string,
  userName: string,
  userPhotoUrl: string | undefined,
  placeId: string,
  placeName: string,
  placeImageUrl: string | undefined,
  rating: number,
  content?: string
): Promise<void> {
  await createActivity(
    {
      type: 'review',
      placeId,
      placeName,
      placeImageUrl,
      rating,
      content: content?.slice(0, 200), // Truncate for activity feed
    },
    userId,
    userName,
    userPhotoUrl
  );
}

export async function logFavoriteActivity(
  userId: string,
  userName: string,
  userPhotoUrl: string | undefined,
  placeId: string,
  placeName: string,
  placeImageUrl?: string
): Promise<void> {
  await createActivity(
    {
      type: 'favorite',
      placeId,
      placeName,
      placeImageUrl,
    },
    userId,
    userName,
    userPhotoUrl
  );
}

export async function logCheckinActivity(
  userId: string,
  userName: string,
  userPhotoUrl: string | undefined,
  placeId: string,
  placeName: string,
  placeImageUrl?: string,
  content?: string
): Promise<void> {
  await createActivity(
    {
      type: 'checkin',
      placeId,
      placeName,
      placeImageUrl,
      content,
    },
    userId,
    userName,
    userPhotoUrl
  );
}

export async function logGroupJoinActivity(
  userId: string,
  userName: string,
  userPhotoUrl: string | undefined,
  groupId: string,
  groupName: string
): Promise<void> {
  await createActivity(
    {
      type: 'group_join',
      groupId,
      groupName,
    },
    userId,
    userName,
    userPhotoUrl
  );
}

// ============ FETCH ACTIVITIES ============

export async function getPublicActivities(
  limitCount: number = 30,
  lastDoc?: DocumentData
): Promise<Activity[]> {
  const constraints: QueryConstraint[] = [
    where('visibility', '==', 'public'),
    orderBy('createdAt', 'desc'),
    limit(limitCount),
  ];

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const q = query(collection(db, ACTIVITIES_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Activity[];
}

export async function getActivitiesForUser(
  userId: string,
  limitCount: number = 20
): Promise<Activity[]> {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Activity[];
}

export async function getActivitiesForPlace(
  placeId: string,
  limitCount: number = 20
): Promise<Activity[]> {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where('placeId', '==', placeId),
    where('visibility', '==', 'public'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Activity[];
}

export async function getActivitiesForGroup(
  groupId: string,
  limitCount: number = 20
): Promise<Activity[]> {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where('groupId', '==', groupId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Activity[];
}

// ============ FILTERED ACTIVITIES ============

export async function getFilteredActivities(
  filters: ActivityFilters,
  limitCount: number = 30
): Promise<Activity[]> {
  const constraints: QueryConstraint[] = [];

  if (filters.types && filters.types.length > 0) {
    constraints.push(where('type', 'in', filters.types));
  }

  if (filters.userId) {
    constraints.push(where('userId', '==', filters.userId));
  }

  if (filters.placeId) {
    constraints.push(where('placeId', '==', filters.placeId));
  }

  if (filters.groupId) {
    constraints.push(where('groupId', '==', filters.groupId));
  }

  if (filters.visibility && filters.visibility.length > 0) {
    constraints.push(where('visibility', 'in', filters.visibility));
  } else {
    constraints.push(where('visibility', '==', 'public'));
  }

  if (filters.since) {
    constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.since)));
  }

  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(limit(limitCount));

  const q = query(collection(db, ACTIVITIES_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Activity[];
}

// ============ REAL-TIME SUBSCRIPTION ============

export function subscribeToPublicActivities(
  callback: (activities: Activity[]) => void,
  limitCount: number = 30
): Unsubscribe {
  const q = query(
    collection(db, ACTIVITIES_COLLECTION),
    where('visibility', '==', 'public'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  return onSnapshot(q, (snapshot) => {
    const activities = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Activity[];
    callback(activities);
  });
}

// ============ ACTIVITY STATS ============

export async function getActivityStats(userId: string): Promise<{
  totalActivities: number;
  reviewCount: number;
  checkinCount: number;
  favoriteCount: number;
}> {
  // This is a simplified implementation
  // For production, consider using aggregation or counters
  const activities = await getActivitiesForUser(userId, 1000);

  return {
    totalActivities: activities.length,
    reviewCount: activities.filter(a => a.type === 'review').length,
    checkinCount: activities.filter(a => a.type === 'checkin').length,
    favoriteCount: activities.filter(a => a.type === 'favorite').length,
  };
}
